import { isBrowser } from 'ws-scripts/modules/environment';
import { addNewRelicPageAction } from '@ddc/new-relic';
import fetchNLSResults from '../util/fetchNLSResults';
import {
	transformInventoryResults,
	makeAllLinkPath
} from '../util/transformInventoryResults';

/**
 * Strategy for Natural Language Search with Condition Tabs enabled
 * Fetches results for NEW, USED, and CERTIFIED conditions in parallel
 */
export async function nlsWithConditionTabsStrategy({
	query,
	widgetSearchServiceUri,
	siteId,
	locale,
	device,
	pageAlias,
	windowId,
	widgetName,
	getPathByCondition,
	processSuggestions,
	pageSearchResults,
	setDidRequestTimeout,
	setRequestTimeoutType,
	accountId,
	searchTerm,
	displayType,
	vehicleOrder,
	prioritizeClosestVehicles,
	userPostalCode
}) {
	const conditionFilters = ['NEW', 'USED', 'CERTIFIED'];

	const fetchConditionResults = async (filter) => {
		const inventorySrpPath = getPathByCondition(filter);

		try {
			const response = await fetchNLSResults(
				widgetSearchServiceUri,
				siteId,
				locale,
				device,
				pageAlias,
				windowId,
				widgetName,
				query,
				vehicleOrder,
				prioritizeClosestVehicles,
				userPostalCode,
				filter
			);

			const { sortedSuggestions, transformedSuggestions } =
				processSuggestions(response, inventorySrpPath);

			return {
				...response,
				allVehiclesPath: makeAllLinkPath(
					sortedSuggestions,
					inventorySrpPath,
					transformedSuggestions,
					response?.nlsFacets
				)
			};
		} catch (error) {
			if (error.name === 'TimeoutError') {
				setDidRequestTimeout(true);
				setRequestTimeoutType('nls');
			}

			if (isBrowser && window?.newrelic) {
				addNewRelicPageAction('ws-site-text-search-fetch-error', {
					errorType: 'fetchNLSResults',
					accountId,
					siteId,
					locale,
					widgetName,
					searchTerm,
					type: displayType.toString(),
					endpoint: `${widgetSearchServiceUri}/nlInventorySearch`,
					conditionFilter: filter || 'all',
					error
				});
			}

			// eslint-disable-next-line no-console
			console.error(
				`NLS fetch failed for ${filter}, using empty results:`,
				error
			);

			return {
				pageResults: [],
				suggestions: [],
				vehicles: []
			};
		}
	};

	const [newResponse, usedResponse, certifiedResponse] = await Promise.all(
		conditionFilters.map(fetchConditionResults)
	);

	const inventorySearchResponse = {
		new: {
			...newResponse,
			suggestions: transformInventoryResults(
				newResponse?.suggestions,
				getPathByCondition('NEW')
			)
		},
		used: {
			...usedResponse,
			suggestions: transformInventoryResults(
				usedResponse?.suggestions,
				getPathByCondition('USED')
			)
		},
		certified: {
			...certifiedResponse,
			suggestions: transformInventoryResults(
				certifiedResponse?.suggestions,
				getPathByCondition('CERTIFIED')
			)
		}
	};

	return {
		inventorySearchResponse,
		pageSearchResponse: pageSearchResults
	};
}
